<template>
  <div
    id="SearchAvailabilityFilters"
    ref="container"
    class="SearchAvailabilityFilters"
    :class="{
      'SearchAvailabilityFilters--small': !isStuck,
      'SearchAvailabilityFilters--offseted': showSearch && isStuck,
    }">
    <div ref="containerSearch" class="SearchAvailabilityFilters-searchbar">
      <search-booking-bar ref="searchBookingBar" />
    </div>

    <search-experience-category-filters />
  </div>
</template>

<script setup lang="ts">
import SearchBookingBar from '../SearchBookingBar/SearchBookingBar.vue';
import SearchExperienceCategoryFilters from '../SearchExperienceCategories/SearchExperienceCategoryFilters.vue';

const searchBookingBar = ref<typeof SearchBookingBar>();

const focusOriginField = () => {
  if (!searchBookingBar.value) return;

  searchBookingBar.value.focusOriginField();
};

const openDatepicker = () => {
  if (!searchBookingBar.value) return;

  searchBookingBar.value.openDatepicker();
};

// Sticky header behaviour
const container = ref<HTMLDivElement>();
const containerSearch = ref<HTMLDivElement>();
const isStuck = ref(false);
const lastScrollPosition = ref(0);
const showSearch = ref(true);
const searchOffset = ref('');

const containerInitialPosition = ref<DOMRect>();

onMounted(() => {
  window.addEventListener('scroll', onScroll);

  if (!containerSearch.value) return;

  searchOffset.value =
    containerSearch.value.getBoundingClientRect().height * -1 + 'px';

  if (container.value) {
    containerInitialPosition.value = container.value.getBoundingClientRect();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
});

const onScroll = () => {
  if (!container.value) return;

  const currentScroll = window.scrollY || document.documentElement.scrollTop;

  /** Prevent search bar from being stuck while scrolling too fast down & up by forcing it to unstuck if scroll
   * position is 0 */
  if (currentScroll === 0) {
    isStuck.value = false;
    return;
  }

  const hasAnimationRunning = container.value.getAnimations().length > 0;

  if (hasAnimationRunning) return;

  const threshold =
    (containerInitialPosition.value?.top ?? 0) +
    (containerInitialPosition.value?.height ?? 0) * 0.15;

  isStuck.value = currentScroll > threshold;

  showSearch.value = Math.sign(lastScrollPosition.value - currentScroll) < 0;

  lastScrollPosition.value = currentScroll;
};

defineExpose({ focusOriginField, openDatepicker });
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/grid';

.SearchAvailabilityFilters {
  position: sticky;
  top: 0;
  transform: translateY(0);
  transition: all 300ms;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;

  &--small {
    @include grid.wrapper(mq.$layout-width, 24px);
    padding-bottom: constants.$padding-00;

    @media (mq.$medium) and (mq.$large) {
      @include grid.wrapper(mq.$layout-width, 32px);
      padding-bottom: constants.$padding-03;
    }

    @media (mq.$from-xlarge) {
      @include grid.wrapper(mq.$layout-width, 64px);
      padding-bottom: constants.$padding-03;
    }
  }

  &--offseted {
    transform: translateY(v-bind(searchOffset));
  }

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    background-color: colors.$white;
    inset: 0;
  }
}

.SearchAvailabilityFilters-searchbar {
  width: 100%;
  padding: constants.$padding-01;
  background-color: colors.$gold-100;
  margin-bottom: constants.$margin-00;

  @media (mq.$from-large) {
    padding-left: constants.$padding-03;
    padding-right: constants.$padding-03;
    padding-bottom: constants.$padding-02;
    padding-top: constants.$padding-02;
    margin-bottom: 0;
  }
}

.SearchAvailabilityFilters-title {
  text-align: center;
  padding-top: 0;

  @media (mq.$from-large) {
    padding-bottom: constants.$padding-03;
  }
}
</style>
